import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from "recharts";
import type { TooltipProps } from "recharts";
import "../css/HourlyConnectionChart.css";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HourlyChartPoint {
  intervalle: string;
  connectes: number;
}

interface DailyPeak {
  date: string;       // "YYYY-MM-DD"
  connected: number;
}

interface Props {
  data:           HourlyChartPoint[];
  selectedPeriod: string;
  dailyPeak?:     DailyPeak;
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

type CustomTooltipProps = TooltipProps<number, string> & {
  active?:  boolean;
  payload?: Array<{ value?: number }>;
  label?:   string;
};

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const value = payload[0].value ?? 0;
  return (
    <div className="hcc-tooltip">
      <span className="hcc-tooltip-label">{label}</span>
      <span className="hcc-tooltip-value">
        <span className="hcc-tooltip-dot" />
        {value.toLocaleString("fr-FR")} connectés
      </span>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPeakIndex(data: HourlyChartPoint[]): number {
  if (!data.length) return -1;
  return data.reduce(
    (maxIdx, item, idx, arr) =>
      item.connectes > arr[maxIdx].connectes ? idx : maxIdx,
    0
  );
}


export default function HourlyConnectionChart({ data, selectedPeriod, dailyPeak }: Props) {
  const [revealed, setRevealed] = useState(false);
  const containerRef            = useRef<HTMLDivElement>(null);

  const peakIndex    = getPeakIndex(data);
  const maxValue     = data[peakIndex]?.connectes ?? 0;
  const peakInterval = data[peakIndex]?.intervalle ?? "—";

  useEffect(() => {
    setRevealed(false);
    const t = setTimeout(() => setRevealed(true), 80);
    return () => clearTimeout(t);
  }, [data]);

  return (
    <div className="hcc-card" ref={containerRef}>

      {/* ── Header ── */}
      <div className="hcc-header">
        <div className="hcc-header-left">
          <h3 className="hcc-title">Connexions par intervalle horaire</h3>
          <p className="hcc-subtitle">Distribution des utilisateurs actifs sur la période</p>
        </div>

        {maxValue > 0 && (
          <div className="hcc-peak-badge">
            <span className="hcc-peak-label">Pic</span>
            <span className="hcc-peak-value">{maxValue.toLocaleString("fr-FR")}</span>
            <span className="hcc-peak-time">{peakInterval}</span>
          </div>
        )}
      </div>

      {/* ── Chart ── */}
      <div className={`hcc-chart-wrap${revealed ? " hcc-chart-wrap--visible" : ""}`}>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart
            data={data}
            margin={{ top: 20, right: 24, bottom: 8, left: 4 }}
          >
            <CartesianGrid
              strokeDasharray="4 4"
              stroke="#edf0f5"
              vertical={false}
            />
            <XAxis
              dataKey="intervalle"
              tick={{ fontSize: 11, fill: "#8a97a8", fontWeight: 500 }}
              interval={1}
              angle={-35}
              textAnchor="end"
              height={60}
              axisLine={{ stroke: "#e5e9ef" }}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#8a97a8" }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: "#e5e9ef", strokeWidth: 1 }}
            />

            {/* Main line */}
            <Line
              type="monotone"
              dataKey="connectes"
              stroke="#2A919D"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#ffffff", stroke: "#2A919D", strokeWidth: 2 }}
              activeDot={{ r: 6, fill: "#2A919D", stroke: "#ffffff", strokeWidth: 2 }}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            />

            {/* Peak dot */}
            {peakIndex >= 0 && maxValue > 0 && (
              <ReferenceDot
                x={peakInterval}
                y={maxValue}
                r={6}
                fill="#eb8635"
                stroke="#ffffff"
                strokeWidth={2}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── Footer legend ── */}
      <div className="hcc-legend">
        <span className="hcc-legend-item">
          <svg width="20" height="10" style={{ marginRight: 2 }}>
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2A919D" strokeWidth="2" />
            <circle cx="10" cy="5" r="3" fill="#ffffff" stroke="#2A919D" strokeWidth="2" />
          </svg>
          Connectés
        </span>
        <span className="hcc-legend-item">
          <svg width="10" height="10" style={{ marginRight: 2 }}>
            <circle cx="5" cy="5" r="5" fill="#eb8635" />
          </svg>
          Pic d&apos;activité
        </span>
      </div>

    </div>
  );
}